"use client"

import { useEffect, useRef } from "react"
import { Modal, Text, Stack, Group, Box, Button, CopyButton, ActionIcon, Alert, Loader, Center } from "@forestsync/ui"
import { TbCopy, TbCheck, TbAlertCircle } from "react-icons/tb"
import { useTranslations } from "next-intl"

interface FundingInstructions {
  iban: string
  bic: string
  accountHolder: string
  reference: string
}

interface DepositFundsModalProps {
  opened: boolean
  onClose: () => void
  fundingInstructions: FundingInstructions | null
  onRetry?: () => void
  isRetrying?: boolean
}

export function DepositFundsModal({ opened, onClose, fundingInstructions, onRetry, isRetrying }: DepositFundsModalProps) {
  const t = useTranslations("dashboard.settings.funding")
  const tc = useTranslations("common")

  const autoFetchedRef = useRef(false)

  useEffect(() => {
    if (!opened) { autoFetchedRef.current = false; return }
    if (!fundingInstructions && !isRetrying && !autoFetchedRef.current) {
      autoFetchedRef.current = true
      onRetry?.()
    }
  }, [opened, isRetrying])

  // Show loader on first open (before effect fires) and while fetching — avoids error flash
  const showLoader = isRetrying || (!autoFetchedRef.current && opened && !fundingInstructions)

  if (!fundingInstructions) {
    return (
      <Modal opened={opened} onClose={onClose} title={t("depositFunds")} size="md" centered>
        {showLoader ? (
          <Center py="xl"><Loader size="md" /></Center>
        ) : (
          <Stack gap="md">
            <Alert color="red" icon={<TbAlertCircle size={16} />} title={t("setupError")}>
              {t("setupErrorDesc")}
            </Alert>
            {onRetry && (
              <Button variant="light" fullWidth onClick={onRetry}>{tc("tryAgain")}</Button>
            )}
            <Button variant="subtle" fullWidth onClick={onClose}>{tc("close")}</Button>
          </Stack>
        )}
      </Modal>
    )
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t("depositFunds")} size="md" centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t("fundingNote")}
        </Text>

        <Box style={{ background: "var(--mantine-color-default)", borderRadius: 6, border: "1px solid var(--mantine-color-default-border)" }} p="md">
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t("accountHolder")}</Text>
              <Text size="sm" fw={600}>{fundingInstructions.accountHolder}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t("iban")}</Text>
              <Group gap="xs">
                <Text size="sm" ff="monospace" fw={600}>{fundingInstructions.iban}</Text>
                <CopyButton value={fundingInstructions.iban} timeout={2000}>
                  {({ copied, copy }) => (
                    <ActionIcon color={copied ? "teal" : "gray"} variant="subtle" onClick={copy}>
                      {copied ? <TbCheck size={14} /> : <TbCopy size={14} />}
                    </ActionIcon>
                  )}
                </CopyButton>
              </Group>
            </Group>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{t("bic")}</Text>
              <Text size="sm" ff="monospace" fw={600}>{fundingInstructions.bic}</Text>
            </Group>
          </Stack>
        </Box>

        <Box style={{ background: "var(--mantine-color-blue-light)", borderRadius: 6 }} p="md">
          <Group justify="space-between">
            <Text size="xs" c="blue" fw={600} tt="uppercase">{t("fundingReference")}</Text>
            <Group gap="xs">
              <Text size="sm" ff="monospace" fw={700} c="blue">{fundingInstructions.reference}</Text>
              <CopyButton value={fundingInstructions.reference} timeout={2000}>
                {({ copied, copy }) => (
                  <ActionIcon color={copied ? "teal" : "blue"} variant="subtle" onClick={copy}>
                    {copied ? <TbCheck size={14} /> : <TbCopy size={14} />}
                  </ActionIcon>
                )}
              </CopyButton>
            </Group>
          </Group>
        </Box>

        <Button fullWidth onClick={onClose}>{tc("done")}</Button>
      </Stack>
    </Modal>
  )
}
